import { useState } from 'preact/hooks';
import { IcoCalendar, IcoCheck, IcoRefresh } from '@components/icons';
import { rpx } from 'client/lib/rpx-client';
import type { PaneProps } from '../../types';
import { useAsyncData } from 'client/lib/hooks';
import { showError } from '@components/app-error';
import { BtnPrimary } from '@components/buttons';
import { WizardFooter } from './wizard-footer';
import { useAuth } from 'client/lib/auth';
import { StatusPill } from './status-pill';

function ProgressStep({
  title,
  status,
}: {
  title: string;
  status: 'completed' | 'active' | 'pending';
}) {
  const statusStyles = {
    completed: {
      text: 'text-green-600',
      icon: (
        <div class="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
          <IcoCheck class="w-3 h-3" />
        </div>
      ),
    },
    active: {
      text: 'text-indigo-600',
      icon: <div class="w-5 h-5 border-2 border-indigo-600 rounded-full animate-pulse"></div>,
    },
    pending: {
      text: 'text-gray-400',
      icon: <div class="w-5 h-5 border-2 border-gray-300 rounded-full"></div>,
    },
  };

  const { text, icon } = statusStyles[status];

  return (
    <div class={`flex items-center gap-3 ${text}`}>
      {icon}
      <span>{title}</span>
    </div>
  );
}

export function ProvisioningStep({
  onNext,
  integrations,
  price,
  calendarPurchase,
  updateCalendarPurchase,
}: PaneProps) {
  const auth = useAuth();
  const [state, setState] = useState({
    retries: 0,
    failed: false,
    processing: false,
  });
  useAsyncData(async () => {
    setState((s) => ({ ...s, processing: true, failed: false }));

    try {
      // Get the price ID from the purchase or default price
      const priceId = calendarPurchase?.price?.id || price?.id;

      if (!priceId) {
        throw new Error('No price ID available for provisioning');
      }

      // Provision with a min delay to show the provisioning state
      await Promise.all([
        new Promise((r) => setTimeout(r, 3000)), // Min 3s to show UI
        // Call provision if there is no active purchase
        calendarPurchase?.purchase?.status === 'active'
          ? Promise.resolve()
          : rpx.calendarCheckout.provision({ priceId }),
      ]);

      // Refresh the current user data to get the updated calendarTier
      const updatedUser = await rpx.auth.getCurrentUser();
      auth.setUser(updatedUser);

      // Update the purchase state to reflect provisioning is complete
      if (updateCalendarPurchase && calendarPurchase?.purchase) {
        updateCalendarPurchase({
          ...calendarPurchase,
          purchase: {
            ...calendarPurchase.purchase,
            status: 'active',
          },
        });
      }

      onNext();
    } catch (err) {
      setState((s) => ({ ...s, failed: true, processing: false }));
      showError(err);
    }
  }, [state.retries, onNext, calendarPurchase, price]);

  const isCalendarConfigured = integrations?.hasGoogle && integrations?.hasZoom;

  return (
    <div class="flex flex-col h-full">
      <div class="flex-1 flex flex-col justify-center items-center gap-8">
        <StatusPill text="Provisioning account" theme="blue" animated />

        {/* Loading indicator with calendar icon */}
        <div class="relative">
          <div
            class={`w-16 h-16 border-4 rounded-full ${
              state.processing && !state.failed
                ? 'border-indigo-200 border-t-indigo-600 animate-spin'
                : 'border-indigo-200'
            }`}
          ></div>
          <div class="absolute inset-0 flex items-center justify-center">
            <IcoCalendar class="w-6 h-6 text-indigo-600" />
          </div>
        </div>

        <div class="text-center max-w-md">
          <h2 class="text-2xl font-bold text-gray-900 mb-3">Setting up your account...</h2>
          <p class="text-gray-600 leading-relaxed mb-6">
            We're configuring your new coaching page and activating all the features you need to
            start taking bookings.
          </p>

          {/* Progress steps */}
          <div class="flex flex-col gap-3 text-sm">
            <ProgressStep title="Payment processed successfully" status="completed" />
            <ProgressStep
              title="Configuring your coaching profile"
              status={isCalendarConfigured ? 'completed' : 'active'}
            />
            <ProgressStep
              title="Configuring domain settings"
              status={isCalendarConfigured ? 'active' : 'pending'}
            />
          </div>
        </div>
      </div>

      <WizardFooter show={state.failed} className="an-fade-in-bottom">
        <BtnPrimary
          onClick={() =>
            setState((s) => ({ failed: false, retries: s.retries + 1, processing: false }))
          }
          class="w-full flex items-center justify-center gap-2"
        >
          <IcoRefresh class="w-4 h-4" />
          Try Again
        </BtnPrimary>
      </WizardFooter>
    </div>
  );
}
